export const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
export const phase = (value, start, end) => clamp((value - start) / (end - start));
export const smooth = value => { const p = clamp(value); return p * p * (3 - 2 * p); };
export const mix = (a, b, progress) => a + (b - a) * progress;

// A tall introduction continues in normal scroll after its animation finishes.
// Its extra reading space must not stretch the animation or its chapter target.
export function homeMotionSpan(section, viewportHeight) {
  const stage = section.querySelector('[data-home-motion-stage]');
  return Math.max(0, section.offsetHeight - (stage ? stage.offsetHeight : viewportHeight ?? window.innerHeight));
}

// A deliberate pace for the opening cue. Any visitor input takes control immediately.
export function homeGlideProgress(progress, tangent = 0) {
  const p = clamp(progress);
  return smooth(p) + clamp(tangent, 0, 3) * p * (1 - p) ** 2;
}

export function glideHomeTo(top, duration = 1500, viewport, velocity = null) {
  const start = window.scrollY;
  const end = clamp(top, 0, Math.max(0, document.documentElement.scrollHeight - innerHeight));
  const started = performance.now();
  const width = window.innerWidth;
  const height = viewport?.clientHeight ?? window.innerHeight;
  // A project settle carries the gesture's speed into its first frame. The
  // introduction keeps its deliberate ease-in, which starts from rest.
  const tangent = velocity === null || end === start ? 0 : clamp(velocity * duration / (end - start), .8, 2.5);
  let frame = 0;
  const events = ['wheel', 'touchstart', 'pointerdown', 'hashchange', 'pagehide'];
  function cancel() {
    cancelAnimationFrame(frame);
    events.forEach(event => window.removeEventListener(event, cancel));
    window.removeEventListener('resize', onResize);
    window.removeEventListener('keydown', onKey);
    document.removeEventListener('visibilitychange', cancel);
  }
  function onKey(event) {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Escape'].includes(event.key)) cancel();
  }
  function onResize() {
    // Browser chrome can resize the visible area during this scroll. Only a
    // change to the page's layout viewport invalidates the chapter destination.
    if (window.innerWidth !== width || (viewport?.clientHeight ?? window.innerHeight) !== height) cancel();
  }
  function step(now) {
    const progress = clamp((now - started) / duration);
    window.scrollTo({ top: mix(start, end, homeGlideProgress(progress, tangent)), behavior: 'instant' });
    if (progress < 1) frame = requestAnimationFrame(step);
    else cancel();
  }
  events.forEach(event => window.addEventListener(event, cancel, { passive: true }));
  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('keydown', onKey);
  document.addEventListener('visibilitychange', cancel);
  frame = requestAnimationFrame(step);
  return cancel;
}

// Native scrolling owns the page. Only the visual state follows it, and only
// while a scroll/resize is settling. No wheel interception or permanent loop.
export function watchHomeMotion(sections, callback, viewport) {
  const preference = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let lastTime = 0;
  let current = window.scrollY;
  let target = current;
  let destroyed = false;
  let bounds = [];
  let viewportHeight = 0;
  function measure() {
    // Match the 100svh stage used by CSS. innerHeight changes as mobile browser
    // controls appear, even though the page's scroll layout has not changed.
    viewportHeight = viewport.clientHeight;
    bounds = sections.map(el => ({ top: el.getBoundingClientRect().top + window.scrollY, span: Math.max(1, homeMotionSpan(el, viewportHeight)) }));
    wake();
  }
  function paint(time) {
    frame = 0;
    if (destroyed || document.hidden) return;
    const delta = Math.min(50, Math.max(1, time - (lastTime || time - 16)));
    lastTime = time;
    target = window.scrollY;
    current = preference.matches ? target : mix(current, target, 1 - Math.exp(-delta / 65));
    if (Math.abs(target - current) < .1) current = target;
    callback(
      bounds.map(({ top, span }) => clamp((current - top) / span)),
      preference.matches,
      bounds.map(({ top }) => clamp((current + viewportHeight * .9 - top) / (viewportHeight * .9))),
    );
    if (current !== target) frame = requestAnimationFrame(paint);
  }
  function wake() {
    target = window.scrollY;
    if (!frame && !document.hidden) frame = requestAnimationFrame(paint);
  }
  const resize = new ResizeObserver(measure);
  resize.observe(viewport);
  sections.forEach(el => resize.observe(el));
  addEventListener('scroll', wake, { passive: true });
  addEventListener('resize', measure, { passive: true });
  document.addEventListener('visibilitychange', wake);
  preference.addEventListener('change', measure);
  measure();
  return () => {
    destroyed = true;
    cancelAnimationFrame(frame);
    resize.disconnect();
    removeEventListener('scroll', wake);
    removeEventListener('resize', measure);
    document.removeEventListener('visibilitychange', wake);
    preference.removeEventListener('change', measure);
  };
}
